import * as THREE from 'three';
import {
  createGlowMaterial,
  createHaloMaterial,
  createMatteMaterial,
  palette
} from '../../visual';
import {
  CEILING_HEIGHT,
  FLOOR_Y,
  INTERIOR_RADIUS
} from './StationLayout';
import type { ShopDefinition } from './ShopKind';

export interface AlcoveBuildResult {
  readonly root: THREE.Group;
  /** World position where the counter should be placed. */
  readonly counterPosition: THREE.Vector3;
  /** Facing angle (radians) for counter.
   *  With this value, the counter's local +Z (where the vendor stands, sign,
   *  etc.) aligns with the outward radial direction — so the vendor looks
   *  back toward the atrium and the player approaches the counter's front
   *  from the center of the station. */
  readonly counterFacing: number;
  readonly obstacles: ReadonlyArray<{
    readonly position: THREE.Vector3;
    readonly radius: number;
  }>;
}

// Alcove protrudes INWARD from the outer wall into the atrium, so it is
// visible from inside the station (the wall is an opaque cylinder at
// r=INTERIOR_RADIUS+0.4 — anything placed outside that radius is occluded).
const ALCOVE_MOUTH_RADIUS = INTERIOR_RADIUS - 11; // 33
const ALCOVE_BACK_RADIUS = INTERIOR_RADIUS - 1;   // 43 — just inside the wall
const ALCOVE_MOUTH_HALF_WIDTH = 3.4;
const ALCOVE_COUNTER_RADIUS = ALCOVE_MOUTH_RADIUS + 0.8; // counter sits just inside the mouth

/**
 * Each shop lives in a booth that protrudes inward from the outer wall. The
 * booth has:
 * - a back wall (near the outer wall) with a glowing shop panel
 * - two side walls extending inward toward the atrium
 * - a lintel/arch at the inward-facing mouth with an illuminated sign
 * - floor inlay on the way in, highlighting the entry
 * - a projector and floating hologram above the counter
 */
export function buildAlcove(
  angle: number,
  def: ShopDefinition
): AlcoveBuildResult {
  const root = new THREE.Group();
  root.name = `alcove:${def.kind}`;
  // Local frame: local +Z maps to world direction (sin(angle), 0, cos(angle)),
  // i.e., the OUTWARD radial direction at the alcove's angle.
  root.rotation.y = angle;

  const obstacles: { position: THREE.Vector3; radius: number }[] = [];

  const darkMat = createMatteMaterial({ color: 0x141828, roughness: 0.75 });
  const hullMat = createMatteMaterial({
    color: palette.station.hullMid,
    roughness: 0.55
  });
  const trimMat = createMatteMaterial({
    color: palette.station.trim,
    roughness: 0.6
  });
  const accentMat = createMatteMaterial({
    color: def.accent,
    roughness: 0.3,
    emissive: def.accent,
    emissiveIntensity: 0.45
  });
  const accentGlow = createGlowMaterial(def.accentGlow, 0.9, true);
  const softGlow = createGlowMaterial(def.accentGlow, 0.55, true);

  const alcoveDepth = ALCOVE_BACK_RADIUS - ALCOVE_MOUTH_RADIUS; // 10
  const alcoveCenterZ = (ALCOVE_MOUTH_RADIUS + ALCOVE_BACK_RADIUS) / 2; // 38
  const halfW = ALCOVE_MOUTH_HALF_WIDTH;

  // --- BACK WALL (near the outer wall) ---------------------------------
  const backHeight = CEILING_HEIGHT - 3;
  const backWidth = halfW * 2 + 3;
  const back = new THREE.Mesh(
    new THREE.BoxGeometry(backWidth, backHeight, 0.3),
    darkMat
  );
  back.position.set(0, backHeight / 2 + FLOOR_Y, ALCOVE_BACK_RADIUS);
  root.add(back);

  // Glowing identity panel mounted on the back wall (faces inward).
  const panelWidth = backWidth * 0.7;
  const panelHeight = backHeight * 0.55;
  const panel = new THREE.Mesh(
    new THREE.PlaneGeometry(panelWidth, panelHeight),
    accentGlow
  );
  panel.rotation.y = Math.PI; // faces the -Z local = inward (player side)
  panel.position.set(
    0,
    panelHeight / 2 + 2 + FLOOR_Y,
    ALCOVE_BACK_RADIUS - 0.18
  );
  root.add(panel);

  const panelFrame = new THREE.Mesh(
    new THREE.PlaneGeometry(panelWidth + 0.5, panelHeight + 0.5),
    createMatteMaterial({ color: 0x0a0d14, roughness: 0.8 })
  );
  panelFrame.rotation.y = Math.PI;
  panelFrame.position.set(
    0,
    panelHeight / 2 + 2 + FLOOR_Y,
    ALCOVE_BACK_RADIUS - 0.22
  );
  root.add(panelFrame);

  // Floor strip along the back.
  const floorStrip = new THREE.Mesh(
    new THREE.BoxGeometry(backWidth, 0.12, 0.24),
    accentMat
  );
  floorStrip.position.set(0, FLOOR_Y + 0.08, ALCOVE_BACK_RADIUS - 0.25);
  root.add(floorStrip);

  // --- SIDE WALLS ------------------------------------------------------
  const sideHeight = CEILING_HEIGHT - 3;
  const sideWidth = 0.3;
  const sideLength = alcoveDepth - 0.3;

  for (const side of [-1, 1] as const) {
    const sideMesh = new THREE.Mesh(
      new THREE.BoxGeometry(sideWidth, sideHeight, sideLength),
      hullMat
    );
    sideMesh.position.set(
      side * (halfW + 0.1),
      sideHeight / 2 + FLOOR_Y,
      alcoveCenterZ
    );
    root.add(sideMesh);

    // Inner accent strip on each side wall (faces the alcove interior).
    const strip = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, sideHeight - 6, sideLength - 1.2),
      accentMat
    );
    strip.position.set(
      side * (halfW - 0.02),
      (sideHeight - 6) / 2 + 1 + FLOOR_Y,
      alcoveCenterZ
    );
    root.add(strip);

    // Obstacles along the side walls (a handful of collider dots so the
    // player can't slip past them diagonally).
    for (let k = 0; k <= 2; k++) {
      const t = k / 2;
      const z_local = ALCOVE_MOUTH_RADIUS + t * alcoveDepth;
      const px = Math.sin(angle) * z_local + Math.cos(angle) * side * halfW;
      const pz = Math.cos(angle) * z_local - Math.sin(angle) * side * halfW;
      obstacles.push({
        position: new THREE.Vector3(px, 0, pz),
        radius: 0.6
      });
    }
  }

  // --- LINTEL / ARCH at the MOUTH (facing atrium) ---------------------
  const lintelHeight = 1.1;
  const lintelWidth = (halfW + 0.6) * 2;
  const lintelY = CEILING_HEIGHT - 3.2 + FLOOR_Y;

  const lintel = new THREE.Mesh(
    new THREE.BoxGeometry(lintelWidth, lintelHeight, 1.5),
    hullMat
  );
  lintel.position.set(0, lintelY, ALCOVE_MOUTH_RADIUS + 0.2);
  root.add(lintel);

  const lintelAccent = new THREE.Mesh(
    new THREE.BoxGeometry(lintelWidth - 1, 0.15, 1.7),
    accentMat
  );
  lintelAccent.position.set(
    0,
    lintelY - lintelHeight / 2,
    ALCOVE_MOUTH_RADIUS + 0.2
  );
  root.add(lintelAccent);

  // Illuminated nameplate below the lintel, facing the atrium.
  const signFrame = new THREE.Mesh(
    new THREE.PlaneGeometry(lintelWidth - 0.6, 0.95),
    createMatteMaterial({ color: 0x0a0d14, roughness: 0.8 })
  );
  signFrame.rotation.y = Math.PI;
  signFrame.position.set(
    0,
    lintelY - lintelHeight / 2 - 0.7,
    ALCOVE_MOUTH_RADIUS - 0.06
  );
  root.add(signFrame);

  const sign = new THREE.Mesh(
    new THREE.PlaneGeometry(lintelWidth - 0.9, 0.72),
    createGlowMaterial(def.accentGlow, 0.95, true)
  );
  sign.rotation.y = Math.PI;
  sign.position.set(
    0,
    lintelY - lintelHeight / 2 - 0.7,
    ALCOVE_MOUTH_RADIUS - 0.04
  );
  root.add(sign);

  // Torch beacons at the mouth corners — frame the opening.
  for (const side of [-1, 1] as const) {
    const beaconColumn = new THREE.Mesh(
      new THREE.CylinderGeometry(0.22, 0.28, 6.0, 12),
      trimMat
    );
    beaconColumn.position.set(
      side * (halfW + 0.35),
      3.0 + FLOOR_Y,
      ALCOVE_MOUTH_RADIUS - 0.35
    );
    root.add(beaconColumn);

    const beaconTop = new THREE.Mesh(
      new THREE.SphereGeometry(0.36, 16, 12),
      accentGlow
    );
    beaconTop.position.set(
      side * (halfW + 0.35),
      6.4 + FLOOR_Y,
      ALCOVE_MOUTH_RADIUS - 0.35
    );
    root.add(beaconTop);

    // Small column collider.
    const px = Math.sin(angle) * (ALCOVE_MOUTH_RADIUS - 0.35) +
      Math.cos(angle) * side * (halfW + 0.35);
    const pz = Math.cos(angle) * (ALCOVE_MOUTH_RADIUS - 0.35) -
      Math.sin(angle) * side * (halfW + 0.35);
    obstacles.push({ position: new THREE.Vector3(px, 0, pz), radius: 0.38 });
  }

  // --- FLOOR INLAY leading into the alcove ---------------------------
  const entryInlay = new THREE.Mesh(
    new THREE.PlaneGeometry(halfW * 1.6, 4.2),
    createMatteMaterial({
      color: def.accent,
      roughness: 0.35,
      emissive: def.accent,
      emissiveIntensity: 0.35
    })
  );
  entryInlay.rotation.x = -Math.PI / 2;
  entryInlay.position.set(
    0,
    FLOOR_Y + 0.02,
    ALCOVE_MOUTH_RADIUS - 2.0
  );
  root.add(entryInlay);

  // Alcove interior floor tint.
  const interiorFloor = new THREE.Mesh(
    new THREE.PlaneGeometry(halfW * 1.9, alcoveDepth - 0.6),
    createMatteMaterial({
      color: 0x1b2238,
      roughness: 0.5,
      emissive: def.accent,
      emissiveIntensity: 0.1
    })
  );
  interiorFloor.rotation.x = -Math.PI / 2;
  interiorFloor.position.set(0, FLOOR_Y + 0.015, alcoveCenterZ);
  root.add(interiorFloor);

  // --- HOLOGRAM FLOATING ABOVE THE COUNTER ----------------------------
  // Pinned high above the counter sign and pushed back against the
  // alcove wall so the new sign label has a clear line of sight from
  // the player's standing position. Without these offsets the holo +
  // halo sit directly between the camera and the sign.
  const holoCore = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.38, 1),
    softGlow
  );
  holoCore.position.set(
    0,
    7.4 + FLOOR_Y,
    ALCOVE_COUNTER_RADIUS + 1.6
  );
  holoCore.name = `alcove-holo:${def.kind}`;
  root.add(holoCore);

  const holoHalo = new THREE.Mesh(
    new THREE.SphereGeometry(0.9, 14, 10),
    createHaloMaterial(def.accentGlow, 0.14)
  );
  holoHalo.position.copy(holoCore.position);
  root.add(holoHalo);

  // Projector disc — sits on the counter top now (y=1.18 ≈ counter
  // surface) and shifted back behind the sign on the radial axis so
  // it doesn't poke into the player's view of the label.
  const projector = new THREE.Mesh(
    new THREE.CylinderGeometry(0.4, 0.48, 0.1, 20),
    trimMat
  );
  projector.position.set(
    0,
    1.18 + FLOOR_Y,
    ALCOVE_COUNTER_RADIUS + 0.5
  );
  root.add(projector);

  const projectorGlow = new THREE.Mesh(
    new THREE.CircleGeometry(0.36, 20),
    softGlow
  );
  projectorGlow.rotation.x = -Math.PI / 2;
  projectorGlow.position.set(
    0,
    1.25 + FLOOR_Y,
    ALCOVE_COUNTER_RADIUS + 0.5
  );
  root.add(projectorGlow);

  // --- SPOTLIGHT CONES from the ceiling into the alcove --------------
  const spotMat = createHaloMaterial(def.accentGlow, 0.09);
  for (const side of [-1, 1] as const) {
    const spot = new THREE.Mesh(
      new THREE.ConeGeometry(1.8, 6.5, 14, 1, true),
      spotMat
    );
    spot.position.set(
      side * 1.6,
      4 + FLOOR_Y,
      alcoveCenterZ
    );
    root.add(spot);
  }

  // --- COMPUTE EXPORTED COUNTER POSITION/FACING -----------------------
  const counterPosition = new THREE.Vector3(
    Math.sin(angle) * ALCOVE_COUNTER_RADIUS,
    0,
    Math.cos(angle) * ALCOVE_COUNTER_RADIUS
  );

  // Counter faces the atrium: its LOCAL +Z (vendor side) points OUTWARD
  // (toward the back wall), so we use the alcove's own angle as the
  // counter rotation. That way the counter's front (the player side, at
  // local -Z) faces the center of the station.
  const counterFacing = angle;

  return {
    root,
    counterPosition,
    counterFacing,
    obstacles
  };
}
